import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const LanguageSettings = () => {
  const navigation = useNavigation();
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [loading, setLoading] = useState(false);

  const colors = {
    primary: '#4F46E5',
    secondary: '#10B981',
    danger: '#EF4444',
    dark: '#1F2937',
    light: '#F9FAFB',
    white: '#FFFFFF',
    gray: '#6B7280',
    lightGray: '#E5E7EB',
    border: '#E5E7EB',
  };

  const languages = [
    {
      id: 'en-US',
      name: 'English',
      nativeName: 'English (US)',
      code: 'en',
      flag: '🇺🇸',
    },
    {
      id: 'es-ES',
      name: 'Spanish',
      nativeName: 'Español',
      code: 'es',
      flag: '🇪🇸',
    },
    {
      id: 'fr-FR',
      name: 'French',
      nativeName: 'Français',
      code: 'fr',
      flag: '🇫🇷',
    },
    {
      id: 'de-DE',
      name: 'German',
      nativeName: 'Deutsch',
      code: 'de',
      flag: '🇩🇪',
    },
    {
      id: 'zh-CN',
      name: 'Chinese',
      nativeName: '中文 (简体)',
      code: 'zh',
      flag: '🇨🇳',
    },
    {
      id: 'ja-JP',
      name: 'Japanese',
      nativeName: '日本語',
      code: 'ja',
      flag: '🇯🇵',
    },
    {
      id: 'ko-KR',
      name: 'Korean',
      nativeName: '한국어',
      code: 'ko',
      flag: '🇰🇷',
    },
    {
      id: 'ar-SA',
      name: 'Arabic',
      nativeName: 'العربية',
      code: 'ar',
      flag: '🇸🇦',
    },
    {
      id: 'ru-RU',
      name: 'Russian',
      nativeName: 'Русский',
      code: 'ru',
      flag: '🇷🇺',
    },
    {
      id: 'pt-BR',
      name: 'Portuguese',
      nativeName: 'Português (Brasil)',
      code: 'pt',
      flag: '🇧🇷',
    },
    {
      id: 'hi-IN',
      name: 'Hindi',
      nativeName: 'हिन्दी',
      code: 'hi',
      flag: '🇮🇳',
    },
    {
      id: 'tr-TR',
      name: 'Turkish',
      nativeName: 'Türkçe',
      code: 'tr',
      flag: '🇹🇷',
    },
  ];

  useEffect(() => {
    loadLanguage();
  }, []);

  const loadLanguage = async () => {
    try {
      const savedLanguage = await AsyncStorage.getItem('app_language');
      if (savedLanguage) {
        setSelectedLanguage(savedLanguage);
      }
    } catch (error) {
      console.error('Error loading language:', error);
    }
  };

  const saveLanguage = async (languageCode) => {
    try {
      await AsyncStorage.setItem('app_language', languageCode);
      setSelectedLanguage(languageCode);
      
      // Show confirmation
      Alert.alert(
        'Language Changed',
        'The app language has been updated. Some changes may require a restart to take effect.',
        [
          { 
            text: 'Restart Now', 
            onPress: () => {
              // In a real app, you might need to restart the app or reload translations
              Alert.alert('Info', 'Please restart the app for changes to fully apply.');
            }
          },
          { 
            text: 'Later', 
            style: 'cancel' 
          }
        ]
      );
    } catch (error) {
      console.error('Error saving language:', error);
      Alert.alert('Error', 'Failed to save language preference');
    }
  };

  const handleLanguageSelect = (languageId) => {
    if (languageId === selectedLanguage) {
      return;
    }

    Alert.alert(
      'Change Language',
      'Are you sure you want to change the app language?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Change', 
          onPress: () => saveLanguage(languageId)
        },
      ]
    );
  };

  const renderLanguageItem = (language) => {
    const isSelected = language.id === selectedLanguage;
    
    return (
      <TouchableOpacity
        style={[
          styles.languageItem,
          isSelected && styles.languageItemSelected,
        ]}
        onPress={() => handleLanguageSelect(language.id)}
      >
        <View style={styles.languageItemLeft}>
          <Text style={styles.flag}>{language.flag}</Text>
          <View style={styles.languageText}>
            <Text style={[
              styles.languageName,
              isSelected && styles.languageNameSelected,
            ]}>
              {language.name}
            </Text>
            <Text style={styles.languageNativeName}>
              {language.nativeName}
            </Text>
          </View>
        </View>
        
        {isSelected && (
          <View style={styles.selectedIndicator}>
            <Feather name="check-circle" size={20} color={colors.primary} />
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={colors.dark} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Language</Text>
          <View style={styles.headerRight} />
        </View>
        <Text style={styles.headerSubtitle}>
          Choose your preferred language for the app interface
        </Text>
      </View>

      <ScrollView 
        showsVerticalScrollIndicator={false} 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
      >
        {/* Current Language */}
        <View style={styles.currentLanguageSection}>
          <Text style={styles.sectionTitle}>Current Language</Text>
          <View style={styles.currentLanguageCard}>
            <Text style={styles.currentLanguageFlag}>
              {languages.find(lang => lang.id === selectedLanguage)?.flag || '🇺🇸'}
            </Text>
            <View style={styles.currentLanguageText}>
              <Text style={styles.currentLanguageName}>
                {languages.find(lang => lang.id === selectedLanguage)?.name || 'English'}
              </Text>
              <Text style={styles.currentLanguageNativeName}>
                {languages.find(lang => lang.id === selectedLanguage)?.nativeName || 'English (US)'}
              </Text>
            </View>
            <View style={styles.currentLanguageBadge}>
              <Text style={styles.currentLanguageBadgeText}>ACTIVE</Text>
            </View>
          </View>
        </View>

        {/* All Languages */}
        <View style={styles.languagesSection}>
          <Text style={styles.sectionTitle}>Available Languages</Text>
          <View style={styles.languagesList}>
            {languages.map((language, index) => (
              <View key={language.id}>
                {renderLanguageItem(language)}
                {index < languages.length - 1 && <View style={styles.separator} />}
              </View>
            ))}
          </View>
        </View>

        {/* Translation Info */}
        <View style={styles.infoSection}>
          <View style={styles.infoIcon}>
            <Feather name="info" size={18} color={colors.primary} />
          </View>
          <View style={styles.infoContent}>
            <Text style={styles.infoTitle}>Translation Status</Text>
            <Text style={styles.infoText}>
              Some languages may have incomplete translations. English is always fully supported.
            </Text>
            <TouchableOpacity style={styles.helpLink}>
              <Text style={styles.helpLinkText}>Help translate this app</Text>
              <Feather name="external-link" size={14} color={colors.primary} style={styles.helpLinkIcon} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Auto-detect Option */}
        <View style={styles.autoDetectSection}>
          <View style={styles.autoDetectHeader}>
            <Feather name="globe" size={18} color={colors.gray} />
            <Text style={styles.autoDetectTitle}>Auto-detect Language</Text>
          </View>
          <Text style={styles.autoDetectDescription}>
            Automatically select language based on your device settings
          </Text>
          <TouchableOpacity 
            style={[
              styles.autoDetectButton,
              selectedLanguage === 'auto' && styles.autoDetectButtonActive,
            ]}
            onPress={() => handleLanguageSelect('auto')}
          >
            <Text style={[
              styles.autoDetectButtonText,
              selectedLanguage === 'auto' && styles.autoDetectButtonTextActive,
            ]}>
              {selectedLanguage === 'auto' ? 'Auto-detect Enabled' : 'Enable Auto-detect'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  headerRight: {
    width: 40,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: 30,
  },
  currentLanguageSection: {
    paddingHorizontal: 20,
    marginTop: 20,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  currentLanguageCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  currentLanguageFlag: {
    fontSize: 32,
    marginRight: 16,
  },
  currentLanguageText: {
    flex: 1,
  },
  currentLanguageName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  currentLanguageNativeName: {
    fontSize: 14,
    color: '#6B7280',
  },
  currentLanguageBadge: {
    backgroundColor: '#10B98110',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  currentLanguageBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#10B981',
  },
  languagesSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  languagesList: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  languageItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  languageItemSelected: {
    backgroundColor: '#4F46E505',
  },
  languageItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  flag: {
    fontSize: 24,
    marginRight: 16,
  },
  languageText: {
    flex: 1,
  },
  languageName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 4,
  },
  languageNameSelected: {
    color: '#4F46E5',
    fontWeight: '600',
  },
  languageNativeName: {
    fontSize: 14,
    color: '#6B7280',
  },
  selectedIndicator: {
    marginLeft: 8,
  },
  separator: {
    height: 1,
    backgroundColor: '#F3F4F6',
    marginHorizontal: 16,
  },
  infoSection: {
    flexDirection: 'row',
    backgroundColor: '#4F46E505',
    marginHorizontal: 20,
    marginBottom: 24,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#4F46E520',
  },
  infoIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#4F46E510',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  infoText: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 18,
    marginBottom: 8,
  },
  helpLink: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  helpLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4F46E5',
    marginRight: 4,
  },
  helpLinkIcon: {
    marginTop: 2,
  },
  autoDetectSection: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  autoDetectHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  autoDetectTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginLeft: 8,
  },
  autoDetectDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 16,
  },
  autoDetectButton: {
    backgroundColor: '#F9FAFB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
  },
  autoDetectButtonActive: {
    backgroundColor: '#4F46E510',
    borderColor: '#4F46E5',
  },
  autoDetectButtonText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#6B7280',
  },
  autoDetectButtonTextActive: {
    color: '#4F46E5',
  },
});

export default LanguageSettings;